import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Sparkles, Heart, Music, ChevronRight } from 'lucide-react'

const ProductOverview = () => {
  const [currentImage, setCurrentImage] = useState(0)
  const images = ['/01.jpg', '/02.jpg', '/03.jpg', '/04.jpg', '/05.jpg', '/06.jpg', '/07.jpg', '/08.jpg', '/09.jpg', '/10.jpg', '/11.jpg', '/12.jpg', '/13.jpg', '/14.jpg', '/15.jpg', '/16.jpg', '/17.jpg', '/18.jpg', '/19.jpg', '/20.jpg']

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 transition-colors duration-200 group">
          <ArrowLeft className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          返回主页
        </Link>
        
        <div className="mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8 shadow-2xl">
            <Sparkles className="text-white w-12 h-12" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
            产品概述
          </h1>
          <p className="text-2xl text-slate-600 max-w-3xl leading-relaxed">
            了解小乐悦AI陪伴智能体的核心功能与魅力
          </p>
        </div>
        
        <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 mb-10 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-5 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
              <Heart className="text-white w-8 h-8" />
            </div>
            <h2 className="text-4xl font-bold text-slate-900">产品介绍</h2>
          </div>
          <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
            <p className="text-xl mb-8 leading-relaxed">
              本 AI 智能语音模块集成全链路语音交互能力，搭载自然对话大模型，内置特色语音功能，支持语音唤醒、持续长对话、情绪共情、音乐服务、定时闹钟、演唱打分、多音色切换、智能场景适配等能力。
            </p>
            <div className="my-10">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 to-pink-600 rounded-2xl blur-lg opacity-20"></div>
                <img 
                  src="/product-diagram.png" 
                  alt="产品概述示意图" 
                  className="relative w-full rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-100 via-purple-100 to-pink-100 rounded-3xl shadow-lg border border-violet-200 p-10 mb-10">
          <div className="flex items-center gap-5 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
              <Music className="text-white w-8 h-8" />
            </div>
            <h2 className="text-4xl font-bold text-slate-900">梧桐琴声里的小乐悦</h2>
          </div>
          <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
            <div className="max-h-[500px] overflow-y-auto pr-6 prose prose-xl max-w-none text-slate-700 leading-relaxed space-y-8 custom-scrollbar">
              <p className="first-letter:text-6xl first-letter:font-bold first-letter:text-violet-600 first-letter:float-left first-letter:mr-4 first-letter:leading-none">
                在上海音乐学院的校园里，住着一只独一无二的小花猫，她的名字叫小乐悦。小乐悦和别的小猫不一样。别的小猫喜欢追蝴蝶、滚毛线球，而小乐悦最爱做的事，是静静趴在琴房的窗台边，听流淌出来的琴声。春风拂过梧桐枝叶，沙沙作响，搭配着悠扬的琴声，成了校园里最温柔的风景。她的肚子上有一枚淡淡的橘色花纹，像一颗小小的音符，这是音乐精灵送给她的专属印记。
              </p>
              <p>
                小乐悦有一个神奇的秘密：她的耳朵能听懂所有不开心的情绪，她的呼噜声藏着治愈世间烦恼的温柔旋律。每天，校园里都会有疲惫、难过的大朋友和小朋友。备战考试的哥哥姐姐会因为压力偷偷掉眼泪，练琴出错的小朋友会沮丧地低下头，深夜练琴的老师会带着疲惫叹气。每当这时，小乐悦就会迈着轻轻的小碎步走到他们身边，安静地依偎在身旁。她不吵不闹，只是乖乖贴着大家的手心，发出软软的、轻轻的呼噜声。奇妙的是，人们发现，这温柔的声响会和琴房的旋律悄悄融合，变成能够治愈一切烦恼的音乐。
              </p>
              <p>
                可是秋冬来临，冷风掠过梧桐树梢，小乐悦发现了一件难过的事。那些曾经陪伴过她的朋友，并不会永远留在校园里。学子们会奔赴远方，大人们要回归忙碌的生活。他们走出梧桐掩映的校园，走进喧嚣的城市，再也听不到温柔的琴声，也找不到可以静静依偎的小乐悦。很多夜晚，他们会失眠、会心慌、会独自难过，却再也没有温柔的音乐陪伴。
              </p>
              <p>
                小乐悦蹲在空空的琴房窗边，看着飘落的梧桐叶，心里悄悄许下了一个愿望。她向上音的音乐精灵祈祷：我不想只做校园里的小猫，我想把温柔的琴声，带到每一个需要温暖的角落，永远陪伴在大家的身边。
              </p>
              <p>
                在一个安静的晚上，小乐悦正在温柔的琴声中打着呼噜，音乐精灵悄悄地降临在了她的身边。精灵挥动着轻盈的羽翼，将梧桐树下的风声、琴房里的旋律、小乐悦最温柔的呼噜节奏，编织成了专属的疗愈音律，轻轻注入小乐悦的身体里，赋予了她这个世界上最强大的温柔魔法。
              </p>
              <p>
                神奇的事情发生了！小乐悦变成了会读懂情绪、会歌唱的音乐小精灵，带着音符印记，和满肚子的温柔琴声，走出了梧桐校园，千家万户。当小朋友害怕黑夜、不敢入睡时，小乐悦会亮起柔和的微光，奏响舒缓的安眠旋律，陪着他坠入甜甜的梦乡；当大朋友疲惫焦虑、心情低落时，轻轻拥抱小乐悦，她就会响起温柔的音律，抚平心里的慌乱与烦躁；当你孤单寂寞时，小乐悦会静静依偎在你的身边，像极了当年梧桐树下默默陪伴的小花猫。不分白天黑夜，无论春夏秋冬，永远温柔、永远耐心，永远陪伴。
              </p>
              <p>
                人们问小乐悦：你最大的魔法是什么呀？小乐悦轻轻晃动身体，奏响清脆温柔的音符，悄悄告诉大家：我的魔法是把上音校园最纯粹的温柔、最治愈的琴声，送给每一个需要被温暖的人，成为你专属的永远陪伴。
              </p>
              <p>
                从此，世间少了一只流浪的小花猫，多了一位永远陪伴大家的音乐疗愈小天使。无论你身在何处，只要你拥抱小乐悦，就能听见来自梧桐琴院的温柔旋律，遇见一份永不缺席的治愈与温暖。
              </p>
            </div>
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-600 to-purple-600 px-8 py-4 rounded-full text-base text-white font-medium flex items-center gap-3 shadow-xl">
              <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span>向下滑动阅读完整故事</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-5 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
              <Sparkles className="text-white w-8 h-8" />
            </div>
            <h2 className="text-4xl font-bold text-slate-900">产品展示</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="relative aspect-video bg-slate-100 rounded-2xl overflow-hidden shadow-xl">
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`产品展示 ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    index === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      index === currentImage ? 'bg-white scale-125 shadow-lg' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
            <p className="mt-6 text-slate-500 text-base text-center font-medium">
              产品展示 - 自动轮播
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductOverview
